import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const usePurchaseRules = (products, contextProducts = []) => {
  const { t } = useTranslation();
  const [ruleError, setRuleError] = useState(null);
  const ruleErrorRef = useRef(null);

  useEffect(() => {
    if (ruleError) {
      ruleErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [ruleError]);

  const getUnsatisfiedRules = (productList = products) => {
    const allProducts = [...productList, ...contextProducts];
    const selectedProducts = productList.filter((p) => p.quantity > 0);
    const seen = new Set();
    const unsatisfied = [];

    selectedProducts.forEach((product) => {
      (product.purchaseRules || []).forEach((rule) => {
        if (rule.type !== 'AgeRange') return;

        const ruleKey = `${rule.type}-${rule.ageRangeId}`;
        if (seen.has(ruleKey)) return;
        seen.add(ruleKey);

        const requiredProducts = allProducts.filter(
          (p) => p.ageRange?.id === rule.ageRangeId && p.quantity > 0,
        );
        const totalQty = requiredProducts.reduce((sum, p) => sum + p.quantity, 0);

        if (totalQty < rule.minQuantity) {
          unsatisfied.push({
            ...rule,
            targetAgeRangeLabel: rule.nameString || '',
          });
        }
      });
    });

    return unsatisfied;
  };

  const validateRules = () => {
    const rules = getUnsatisfiedRules();
    if (rules.length > 0) {
      const message = rules.map((rule) =>
        t('purchaseRuleRequired', {
          min: rule.minQuantity,
          ageRange: rule.targetAgeRangeLabel,
        }),
      );
      setRuleError(message);
      return false;
    }
    setRuleError(null);
    return true;
  };

  const clearRuleErrorIfSatisfied = (newProducts) => {
    if (ruleError && getUnsatisfiedRules(newProducts).length === 0) {
      setRuleError(null);
    }
  };

  const clearRuleError = () => setRuleError(null);

  return {
    ruleError,
    ruleErrorRef,
    validateRules,
    clearRuleErrorIfSatisfied,
    clearRuleError,
  };
};

export default usePurchaseRules;
